import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import { cs, en } from 'vuetify/locale'

/**
 * A Material 3 light scheme, generated with Material Color Utilities (SchemeVibrant) from the FBMI blue #1D5FA8.
 * Pages sit on the tinted `background` (surface-container-low); cards and bars on white `surface`
 * (surface-container-lowest). The container roles fill selected and emphasised states; `green` and `orange`
 * are custom colours harmonised with the key colour (their standard tonal palettes), for the course types.
 */
export const vuetify = createVuetify({
    locale: { locale: 'cs', fallback: 'en', messages: { cs, en } },
    theme: {
        defaultTheme: 'light',
        themes: {
            light: {
                dark: false,
                colors: {
                    background: '#F0F3FF',
                    'on-background': '#171C24',
                    surface: '#FFFFFF',
                    'on-surface': '#171C24',
                    'on-surface-variant': '#404753',
                    'surface-bright': '#F9F9FF',
                    'surface-dim': '#D6DAE6',
                    'surface-container-lowest': '#FFFFFF',
                    'surface-container-low': '#F0F3FF',
                    'surface-container': '#E9EEFA',
                    'surface-container-high': '#E4E8F4',
                    'surface-container-highest': '#DEE2EF',
                    outline: '#717785',
                    'outline-variant': '#C0C7D5',
                    primary: '#005FB0',
                    'on-primary': '#FFFFFF',
                    'primary-container': '#D5E3FF',
                    'on-primary-container': '#004787',
                    secondary: '#555D7E',
                    'on-secondary': '#FFFFFF',
                    'secondary-container': '#DCE1FF',
                    'on-secondary-container': '#3E4565',
                    tertiary: '#5D598A',
                    'on-tertiary': '#FFFFFF',
                    'tertiary-container': '#E4DFFF',
                    'on-tertiary-container': '#454171',
                    error: '#BA1A1A',
                    'on-error': '#FFFFFF',
                    'error-container': '#FFDAD6',
                    'on-error-container': '#93000A',
                    green: '#176B53',
                    'green-container': '#A5F2D4',
                    'on-green-container': '#00513D',
                    orange: '#8F4C33',
                    'orange-container': '#FFDBCF',
                    'on-orange-container': '#72361E',
                    success: '#176B53',
                    info: '#005FB0',
                    'inverse-surface': '#2B313A',
                    'inverse-on-surface': '#ECF1FD',
                    'inverse-primary': '#A6C8FF',
                },
                variables: {
                    'border-opacity': 0.12,
                    'medium-emphasis-opacity': 0.66,
                },
            },
            // The same seed in dark: pages on `surface`, cards and bars one step up on surface-container.
            dark: {
                dark: true,
                colors: {
                    background: '#0E141C',
                    'on-background': '#DEE2EF',
                    surface: '#1B2029',
                    'on-surface': '#DEE2EF',
                    'on-surface-variant': '#C0C7D5',
                    'surface-bright': '#343943',
                    'surface-dim': '#0E141C',
                    'surface-container-lowest': '#090E17',
                    'surface-container-low': '#171C24',
                    'surface-container': '#1B2029',
                    'surface-container-high': '#252A33',
                    'surface-container-highest': '#30353E',
                    outline: '#8A919F',
                    'outline-variant': '#404753',
                    primary: '#A6C8FF',
                    'on-primary': '#00315F',
                    'primary-container': '#004787',
                    'on-primary-container': '#D5E3FF',
                    secondary: '#BDC5EB',
                    'on-secondary': '#272F4D',
                    'secondary-container': '#3E4565',
                    'on-secondary-container': '#DCE1FF',
                    tertiary: '#C6C0F9',
                    'on-tertiary': '#2E2A59',
                    'tertiary-container': '#454171',
                    'on-tertiary-container': '#E4DFFF',
                    error: '#FFB4AB',
                    'on-error': '#690005',
                    'error-container': '#93000A',
                    'on-error-container': '#FFDAD6',
                    green: '#89D6B9',
                    'green-container': '#00513D',
                    'on-green-container': '#A5F2D4',
                    orange: '#FFB59B',
                    'orange-container': '#72361E',
                    'on-orange-container': '#FFDBCF',
                    success: '#89D6B9',
                    info: '#A6C8FF',
                    'inverse-surface': '#DEE2EF',
                    'inverse-on-surface': '#2B313A',
                    'inverse-primary': '#005FB0',
                },
                variables: {
                    'border-opacity': 0.16,
                    'medium-emphasis-opacity': 0.7,
                },
            },
        },
    },
    // Every control and surface shares one 8 px corner (Material 3's square shape), so buttons, toggle
    // groups, dropdowns, fields and the cards holding them line up.
    defaults: {
        VBtn: { rounded: 'lg' },
        // Inside a group only the group's outer corners are rounded, so its buttons stay joined.
        VBtnToggle: { rounded: 'lg', VBtn: { rounded: 0 } },
        VBtnGroup: { rounded: 'lg', VBtn: { rounded: 0 } },
        VTextField: { rounded: 'lg' },
        VSelect: { rounded: 'lg' },
        VAutocomplete: { rounded: 'lg' },
        VCombobox: { rounded: 'lg' },
        VCard: { rounded: 'lg' },
        VAlert: { rounded: 'lg' },
        VChip: { rounded: 'lg' },
        VMenu: { VList: { density: 'comfortable', rounded: 'lg' } },
        // Material 3 plain tooltips sit on the inverse surface. Vuetify's own pair (surface-variant / on-surface-variant)
        // does not fit the M3 scheme, where on-surface-variant is a dark text colour for light surfaces.
        VTooltip: { location: 'top', openDelay: 250, contentClass: 'bg-inverse-surface text-inverse-on-surface' },
    },
})
